import { Archive, CornerUpLeft, MailOpen, ShieldAlert, Star, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { useShell } from "../core/shell";
import type {
  Attachment,
  EmailMessage,
  MailShellSnapshot,
  ShellDownloadedBlob,
  ThreadGroup,
} from "../core/shell-types";
import type { ComposerSeed } from "./Composer";
import {
  addressListLabel,
  bodyText,
  fullReceivedLabel,
  senderHue,
  senderInitials,
  senderLabel,
} from "./format";
import { HtmlBody } from "./HtmlBody";

function EmailBody({ email, hydrating }: { email: EmailMessage; hydrating: boolean }) {
  const text = bodyText(email, email.textBody);
  const html = bodyText(email, email.htmlBody);
  // Like the native apps, HTML is the preferred rendering when the message
  // carries one (multipart/alternative almost always does); the plain part
  // stays one click away.
  // Keyed by email id at the call site, so this resets per message.
  const [preferPlain, setPreferPlain] = useState(false);
  if (html !== null && !(preferPlain && text !== null)) {
    return (
      <div className="flex flex-col gap-2">
        <HtmlBody email={email} html={html} />
        {text !== null ? (
          <button
            type="button"
            className="self-start text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setPreferPlain(true)}
          >
            Show plain text
          </button>
        ) : null}
      </div>
    );
  }
  if (text !== null) {
    return (
      <div className="flex flex-col gap-2">
        <pre className="whitespace-pre-wrap break-words font-sans text-[15px] leading-relaxed text-foreground/90">
          {text}
        </pre>
        {html !== null ? (
          <button
            type="button"
            className="self-start text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setPreferPlain(false)}
          >
            Show HTML
          </button>
        ) : null}
      </div>
    );
  }
  if (hydrating || !email.bodyValues) {
    // The core lists messages before it hydrates bodies; the snapshot fills
    // in once hydration lands.
    return <p className="text-sm text-muted-foreground">Loading message…</p>;
  }
  return <p className="text-sm text-muted-foreground">This message has no readable body.</p>;
}

function ToolbarButton({
  label,
  onClick,
  icon: Icon,
  destructive,
  active,
}: {
  label: string;
  onClick: () => void;
  icon: typeof Archive;
  destructive?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={cn(
        "flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm transition-colors",
        destructive
          ? "text-destructive hover:bg-destructive/10"
          : "text-foreground/80 hover:bg-accent hover:text-foreground",
      )}
    >
      <Icon aria-hidden className={active ? "h-4 w-4 fill-amber-400 text-amber-400" : "h-4 w-4"} />
      <span className="hidden lg:inline">{label}</span>
    </button>
  );
}

// The core hands attachment bytes over base64-encoded (JMAPDownloadedBlob);
// turn them into a Blob the browser can save.
function blobFromDownload(download: ShellDownloadedBlob): Blob {
  const binary = atob(download.base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: download.contentType || "application/octet-stream" });
}

export function seedReply(email: EmailMessage): ComposerSeed {
  const source = email.replyTo?.length ? email.replyTo : email.from;
  const subject = email.subject ?? "";
  const text = bodyText(email, email.textBody) ?? "";
  return {
    to: addressListLabel(source),
    subject: /^re:/i.test(subject) ? subject : `Re: ${subject}`,
    inReplyTo: email.messageId ?? undefined,
    references: [...(email.references ?? []), ...(email.messageId ?? [])],
    body: `\n\nOn ${fullReceivedLabel(email.receivedAt)}, ${senderLabel(email)} wrote:\n${text
      .split("\n")
      .map((line) => `> ${line}`)
      .join("\n")}`,
  };
}

export function MessageView({
  compact = false,
  snapshot,
  thread,
  onArchive,
  onTrash,
  onJunk,
  onToggleRead,
  onToggleStar,
  onReply,
}: {
  // Compact: the pane fills the width; the subject moves from the toolbar
  // (icons only, scrollable) to a heading above the messages.
  compact?: boolean;
  snapshot: MailShellSnapshot;
  thread: ThreadGroup | null;
  onArchive: (thread: ThreadGroup) => void;
  onTrash: (thread: ThreadGroup) => void;
  onJunk: (thread: ThreadGroup) => void;
  onToggleRead: (thread: ThreadGroup) => void;
  onToggleStar: (thread: ThreadGroup) => void;
  onReply: (seed: ComposerSeed) => void;
}) {
  const shell = useShell();
  const [downloading, setDownloading] = useState<string | null>(null);
  // Attachment failures show next to the attachment, not in the shell banner.
  const [downloadError, setDownloadError] = useState<{ blobId: string; message: string } | null>(
    null,
  );
  // Blob URLs handed to the browser's download; revoked when replaced and on
  // unmount (revoking right after click() can cancel the save in some
  // browsers).
  const blobUrls = useRef(new Map<string, string>());
  useEffect(
    () => () => {
      for (const url of blobUrls.current.values()) {
        URL.revokeObjectURL(url);
      }
      blobUrls.current.clear();
    },
    [],
  );

  // The snapshot lists the selected thread newest-first; read top-down.
  const emails = useMemo(
    () =>
      [...snapshot.selectedThreadEmails].sort((a, b) => a.receivedAt.localeCompare(b.receivedAt)),
    [snapshot.selectedThreadEmails],
  );

  // Attachments come through the core (it holds the credentials and the
  // JMAP download endpoint): {op:'downloadAttachment'} → base64 → Blob URL.
  const download = useCallback(
    async (attachment: Attachment) => {
      setDownloading(attachment.blobId);
      setDownloadError(null);
      try {
        const { result } = await shell.command({ op: "downloadAttachment", attachment });
        if (!result || typeof result.base64 !== "string") {
          throw new Error("The core returned no attachment data.");
        }
        const url = URL.createObjectURL(blobFromDownload(result));
        const previous = blobUrls.current.get(attachment.blobId);
        if (previous) {
          URL.revokeObjectURL(previous);
        }
        blobUrls.current.set(attachment.blobId, url);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = result.name || attachment.name || "attachment";
        anchor.click();
      } catch (cause) {
        setDownloadError({
          blobId: attachment.blobId,
          message: cause instanceof Error ? cause.message : String(cause),
        });
      } finally {
        setDownloading(null);
      }
    },
    [shell],
  );

  if (!thread || emails.length === 0) {
    return (
      <section
        aria-label="Message"
        className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 text-muted-foreground"
      >
        <p className="font-display text-lg">Nothing open</p>
        <p className="text-sm">Select a conversation, or press ⏎ on a highlighted one.</p>
      </section>
    );
  }

  const latest = emails[emails.length - 1];

  const subject = latest.subject?.trim() || "(no subject)";

  return (
    <section aria-label="Message" className="flex min-w-0 flex-1 flex-col overflow-x-hidden">
      <div
        className={cn(
          "flex items-center gap-1 border-b border-border/70 py-2.5",
          compact ? "justify-between overflow-x-auto px-3" : "px-5",
        )}
      >
        {compact ? null : (
          <h2 className="min-w-0 flex-1 truncate font-display text-lg tracking-tight">{subject}</h2>
        )}
        <ToolbarButton
          label="Reply"
          icon={CornerUpLeft}
          onClick={() => onReply(seedReply(latest))}
        />
        <ToolbarButton
          label={thread.hasUnread ? "Mark read" : "Mark unread"}
          icon={MailOpen}
          onClick={() => onToggleRead(thread)}
        />
        <ToolbarButton
          label={thread.hasStarred ? "Unstar" : "Star"}
          icon={Star}
          active={thread.hasStarred}
          onClick={() => onToggleStar(thread)}
        />
        <ToolbarButton label="Archive" icon={Archive} onClick={() => onArchive(thread)} />
        <ToolbarButton label="Junk" icon={ShieldAlert} onClick={() => onJunk(thread)} />
        <ToolbarButton label="Trash" icon={Trash2} destructive onClick={() => onTrash(thread)} />
      </div>
      <div className={cn("min-h-0 flex-1 overflow-y-auto py-4", compact ? "px-4" : "px-5")}>
        {compact ? (
          <h2 className="mb-4 break-words font-display text-xl leading-snug tracking-tight">
            {subject}
          </h2>
        ) : null}
        {emails.map((email, index) => (
          <article
            key={email.id}
            className={index > 0 ? "mt-4 border-t border-border/60 pt-5" : undefined}
          >
            <header className="mb-3 flex items-start gap-3">
              <span
                aria-hidden
                className="flex h-10 w-10 shrink-0 select-none items-center justify-center rounded-full text-sm font-semibold text-white"
                style={{ backgroundColor: `oklch(0.55 0.13 ${senderHue(email)})` }}
              >
                {senderInitials(email)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-semibold">{senderLabel(email)}</span>
                  <time
                    dateTime={email.receivedAt}
                    className="shrink-0 text-xs tabular-nums text-muted-foreground"
                  >
                    {fullReceivedLabel(email.receivedAt)}
                  </time>
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  To: {addressListLabel(email.to)}
                  {email.cc?.length ? ` · Cc: ${addressListLabel(email.cc)}` : ""}
                </span>
              </span>
            </header>
            <EmailBody key={email.id} email={email} hydrating={snapshot.isRefreshing} />
            {email.attachments?.length ? (
              <>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {email.attachments.map((attachment) => (
                    <li key={attachment.blobId}>
                      <button
                        type="button"
                        disabled={downloading === attachment.blobId}
                        aria-invalid={downloadError?.blobId === attachment.blobId || undefined}
                        onClick={() => void download(attachment)}
                        className="rounded-md border border-input px-2.5 py-1.5 text-xs transition-colors hover:bg-accent disabled:opacity-50 aria-[invalid]:border-destructive/60"
                      >
                        {downloading === attachment.blobId
                          ? "Downloading…"
                          : (attachment.name ?? "attachment")}
                        {attachment.size && downloading !== attachment.blobId ? (
                          <span className="text-muted-foreground">
                            {" "}
                            · {Math.ceil(attachment.size / 1024)} KB
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
                {downloadError &&
                email.attachments.some(
                  (attachment) => attachment.blobId === downloadError.blobId,
                ) ? (
                  <p role="alert" className="mt-2 text-xs text-destructive">
                    Couldn’t download attachment: {downloadError.message}
                  </p>
                ) : null}
              </>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}
